"use client";
/* Button — magnetic hover with trailing icon island */
import React from "react";

interface ButtonProps {
  className?: string;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  href?: string;
  type?: "button" | "submit" | "reset";
}

export const Button = ({
  className,
  children,
  variant = "primary",
  size = "md",
  href,
  type = "button",
}: ButtonProps) => {
  const sizeClasses = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base",
  }[size];

  const baseClasses = [
    "group inline-flex items-center gap-3 rounded-full font-semibold",
    "transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]",
    "active:scale-[0.97]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]",
    sizeClasses,
    className,
  ].join(" ");

  const variantClasses = {
    primary: [
      "bg-[var(--color-ink)] text-white",
      "hover:bg-[var(--color-accent)] hover:shadow-[0_8px_32px_rgba(15,76,74,0.25)]",
    ].join(" "),
    secondary: [
      "bg-white text-[var(--color-ink)] border border-[var(--color-border)]",
      "hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]",
    ].join(" "),
    ghost: [
      "bg-transparent text-[var(--color-muted)]",
      "hover:text-[var(--color-accent)] hover:bg-[var(--color-accent)]/5",
    ].join(" "),
  };

  const classes = `${baseClasses} ${variantClasses[variant]}`;

  const content = (
    <>
      {children}
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/5 text-[10px] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-white/20 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
        ↗
      </span>
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes}>
        {content}
      </a>
    );
  }

  return (
    <button className={classes} type={type}>
      {content}
    </button>
  );
};
